// src/components/Sidebar.js
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext';
import './Sidebar.css';

function Sidebar({ activeTab, onNavigation, isSidebarOpen, onToggleSidebar }) {
  const [showModifySubMenu, setShowModifySubMenu] = useState(false);
  const [showTasksSubMenu, setShowTasksSubMenu] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [deviceType, setDeviceType] = useState('desktop');
  const [hoveredItem, setHoveredItem] = useState(null);
  const { currentTheme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Detect device type
  useEffect(() => {
    const checkDeviceType = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setDeviceType('mobile'); // Small phones
      } else if (width <= 768) {
        setDeviceType('tablet'); // Tablets
      } else if (width <= 1024) {
        setDeviceType('laptop'); // Small laptops
      } else {
        setDeviceType('desktop'); // Large screens
      }
    };

    checkDeviceType();
    window.addEventListener('resize', checkDeviceType);
    return () => window.removeEventListener('resize', checkDeviceType);
  }, []);

  // Auto-close submenus when sidebar closes on mobile/tablet
  useEffect(() => {
    if ((deviceType === 'mobile' || deviceType === 'tablet') && !isSidebarOpen) {
      setShowModifySubMenu(false);
      setShowTasksSubMenu(false);
    }
  }, [isSidebarOpen, deviceType]);

  useEffect(() => {
    const role = sessionStorage.getItem('userRole');
    console.log('Sidebar - User role:', role);
    setUserRole(role || '');
  }, []);

  // Listen for storage changes
  useEffect(() => {
    const handleStorageChange = () => {
      const role = sessionStorage.getItem('userRole');
      setUserRole(role || '');
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Menu items configuration - CLIENT ONLY MENU
  const clientMenuItems = [
    { id: 'my-projects', icon: 'fas fa-project-diagram', label: 'My Projects', path: '/my-projects' },
    { id: 'pending-projects', icon: 'fas fa-clock', label: 'Pending Projects', path: '/pending-projects' },
    { id: 'completed-projects', icon: 'fas fa-check-circle', label: 'Completed Projects', path: '/completed-projects' }
  ];

  // Admin menu items
  const adminMenuItems = [
    { id: 'dashboard', icon: 'fas fa-tachometer-alt', label: 'Dashboard', path: '/dashboard' },
    { id: 'check-in-out', icon: 'fas fa-clock', label: 'Check-In/Out', path: '/check-in-out' },
    { id: 'tasks', icon: 'fas fa-tasks', label: 'Tasks' },
    { id: 'add-users', icon: 'fas fa-user-plus', label: 'Add Users', path: '/add-users' },
    { id: 'modify-users', icon: 'fas fa-edit', label: 'Modify Users' },
    { id: 'checkinout-records', icon: 'fas fa-list-alt', label: 'All Records', path: '/checkinout-records' },
  ];

  // Manager menu items
  const managerMenuItems = [
    { id: 'dashboard', icon: 'fas fa-tachometer-alt', label: 'Dashboard', path: '/dashboard' },
    { id: 'check-in-out', icon: 'fas fa-clock', label: 'Check-In/Out', path: '/check-in-out' },
    { id: 'tasks', icon: 'fas fa-tasks', label: 'Tasks' },
    { id: 'add-users', icon: 'fas fa-user-plus', label: 'Add Users', path: '/add-users' },
    { id: 'modify-clients', icon: 'fas fa-user-friends', label: 'Modify Clients', path: '/modify-clients' },
    { id: 'modify-editors', icon: 'fas fa-edit', label: 'Modify Editors', path: '/modify-editors' },
    { id: 'modify-operators', icon: 'fas fa-cogs', label: 'Modify Operators', path: '/modify-operators' },
    { id: 'modify-designers', icon: 'fas fa-palette', label: 'Modify Designers', path: '/modify-designers' },
  ];

  // Designer menu items
  const designerMenuItems = [
    { id: 'check-in-out', icon: 'fas fa-clock', label: 'Check-In/Out', path: '/check-in-out' },
    { id: 'designer-dashboard', icon: 'fas fa-palette', label: 'Designer Dashboard', path: '/designer-dashboard' },
    { id: 'my-Admin', icon: 'fas fa-palette', label: 'Admin Tasks', path: '/my-work' },
    { id: 'editor-tasks-with-designers', icon: 'fas fa-users-cog', label: 'Tasks', path: '/editor-tasks-with-designers' },
  ];

  // Tasks submenu
  const tasksSubMenuItems = [
    { id: 'all-tasks', icon: 'fas fa-layer-group', label: 'All Tasks', path: '/all-tasks' },
    { id: 'assigned-tasks', icon: 'fas fa-user-check', label: 'Assigned Tasks', path: '/assigned-tasks' },
    { id: 'unassigned-tasks', icon: 'fas fa-user-times', label: 'Unassigned Tasks', path: '/unassigned-tasks' },
    { id: 'tasks-by-user', icon: 'fas fa-users-cog', label: 'Tasks by Team', path: '/tasks-by-user' },
    { id: 'designer-tasks', icon: 'fas fa-palette', label: 'Designer Tasks', path: '/designer-tasks' },
    { id: 'all-designer-tasks', icon: 'fas fa-palette', label: 'All Designer Tasks', path: '/all-designer-tasks' },
    { id: 'all-editor-tasks', icon: 'fas fa-tasks', label: 'All Editor Tasks', path: '/all-editor-tasks' },
    { id: 'unassigned-editor-tasks', icon: 'fas fa-user-times', label: 'Editor Tasks', path: '/unassigned-editor-tasks' }
  ];

  // Modify submenu
  const modifySubMenuItems = [
    { id: 'modify-clients', icon: 'fas fa-user-friends', label: 'Clients', path: '/modify-clients' },
    { id: 'modify-editors', icon: 'fas fa-edit', label: 'Editors', path: '/modify-editors' },
    { id: 'modify-operators', icon: 'fas fa-cogs', label: 'Operators', path: '/modify-operators' },
    { id: 'modify-managers', icon: 'fas fa-user-tie', label: 'Managers', path: '/modify-managers' },
    { id: 'modify-admin', icon: 'fas fa-user-shield', label: 'Admins', path: '/modify-admin' },
    { id: 'modify-designers', icon: 'fas fa-palette', label: 'Designers', path: '/modify-designers' },
  ];

  const operatorMenuItems = [
    { id: 'dashboard', icon: 'fas fa-tachometer-alt', label: 'Dashboard', path: '/dashboard' },
    { id: 'check-in-out', icon: 'fas fa-clock', label: 'Check-In/Out', path: '/check-in-out' },
    { id: 'operator-tasks', icon: 'fas fa-tasks', label: 'Tasks' }
  ];

  const operatorTasksSubMenuItems = [
    { id: 'new-tasks', icon: 'fas fa-plus-circle', label: 'New Task', path: '/new-tasks' },
    { id: 'view-tasks', icon: 'fas fa-list', label: 'View Tasks', path: '/view-tasks' },
  ];

  const editorMenuItems = [
    { id: 'dashboard', icon: 'fas fa-tachometer-alt', label: 'Dashboard', path: '/dashboard' },
    { id: 'check-in-out', icon: 'fas fa-clock', label: 'Check-In/Out', path: '/check-in-out' },
    { id: 'my-tasks', icon: 'fas fa-tasks', label: 'My Tasks', path: '/my-tasks' },
    { id: 'pending-tasks', icon: 'fas fa-clock', label: 'Pending Tasks', path: '/pending-tasks' },
    { id: 'completed-tasks', icon: 'fas fa-check-circle', label: 'Completed Tasks', path: '/completed-tasks' },
    { id: 'editor-tasks-pool', icon: 'fas fa-users', label: 'Editor Tasks Pool', path: '/editor-tasks-pool' }
  ];

  const getMenuItems = () => {
    if (userRole === 'user' || userRole === 'client') {
      return clientMenuItems;
    }
    
    switch(userRole) {
      case 'admin':
        return adminMenuItems;
      case 'manager':
        return managerMenuItems;
      case 'operator':
        return operatorMenuItems;
      case 'editor':
        return editorMenuItems;
      case 'designer':
        return designerMenuItems;
      default:
        return clientMenuItems;
    }
  };

  const getSubMenuItems = (parentId) => {
    switch(parentId) {
      case 'tasks':
        return tasksSubMenuItems;
      case 'operator-tasks':
        return operatorTasksSubMenuItems;
      case 'modify-users':
        return userRole === 'admin' ? modifySubMenuItems : [];
      default:
        return [];
    }
  };

  const getActiveTabFromRoute = () => {
    const path = location.pathname;
    
    // Client project routes
    if (path === '/my-projects') return 'my-projects';
    if (path === '/pending-projects') return 'pending-projects';
    if (path === '/completed-projects') return 'completed-projects';
    
    // Other routes
    if (path === '/dashboard') return 'dashboard';
    if (path === '/my-tasks') return 'my-tasks';
    if (path === '/my-work') return 'my-work';
    if (path === '/all-tasks') return 'all-tasks';
    if (path === '/assigned-tasks') return 'assigned-tasks';
    if (path === '/unassigned-tasks') return 'unassigned-tasks';
    if (path === '/tasks-by-user') return 'tasks-by-user';
    if (path === '/designer-tasks') return 'designer-tasks';
    if (path === '/add-users') return 'add-users';
    if (path === '/new-tasks') return 'new-tasks';
    if (path === '/view-tasks') return 'view-tasks';
    if (path === '/operator-dashboard') return 'operator-dashboard';
    if (path === '/check-in-out') return 'check-in-out';
    if (path === '/checkinout-records') return 'checkinout-records';
    
    if (path.startsWith('/edit-task/')) return 'my-tasks';
    if (path.startsWith('/modify-')) return path.replace('/', '');
    if (path.startsWith('/update-task/')) return 'view-tasks';
    if (path.startsWith('/editor/edit-task/')) return 'my-tasks';
    
    return 'my-projects';
  };

  const currentActiveTab = activeTab || getActiveTabFromRoute();

  const handleNavItemClick = (item) => {
    const hasSubMenu = (item.id === 'modify-users' && userRole === 'admin') || 
                      (item.id === 'tasks' && (userRole === 'admin' || userRole === 'manager')) ||
                      (item.id === 'operator-tasks' && userRole === 'operator');
    
    if (hasSubMenu) {
      if (item.id === 'tasks') {
        setShowTasksSubMenu(!showTasksSubMenu);
        setShowModifySubMenu(false);
      } else if (item.id === 'modify-users') {
        setShowModifySubMenu(!showModifySubMenu);
        setShowTasksSubMenu(false);
      } else if (item.id === 'operator-tasks') {
        setShowTasksSubMenu(!showTasksSubMenu);
        setShowModifySubMenu(false);
      }
    } else if (item.path) {
      navigate(item.path);
      // Auto-close sidebar on mobile/tablet after navigation
      if (deviceType === 'mobile' || deviceType === 'tablet') {
        onToggleSidebar();
        // Close any open submenus
        setShowModifySubMenu(false);
        setShowTasksSubMenu(false);
      }
    }
  };

  const handleSubMenuItemClick = (subItem) => {
    navigate(subItem.path);
    // Auto-close sidebar on mobile/tablet after navigation
    if (deviceType === 'mobile' || deviceType === 'tablet') {
      onToggleSidebar();
      // Close submenus
      setShowModifySubMenu(false);
      setShowTasksSubMenu(false);
    }
  };

  const primaryColor = currentTheme?.primary || '#0d6efd';
  const secondaryColor = currentTheme?.secondary || '#6f42c1';

  const getSidebarWidth = () => {
    if (!isSidebarOpen) return '70px';
    
    switch(deviceType) {
      case 'mobile':
        return '85%'; // Use percentage for mobile
      case 'tablet':
        return '300px';
      case 'laptop':
        return '280px';
      default:
        return '280px';
    }
  };

  const getIconSize = () => {
    switch(deviceType) {
      case 'mobile':
        return '1.2rem';
      case 'tablet':
        return '1.1rem';
      default:
        return '1rem';
    }
  };

  const getFontSize = () => {
    switch(deviceType) {
      case 'mobile':
        return '1rem';
      case 'tablet':
        return '0.95rem';
      default:
        return '0.9rem';
    }
  };

  const renderSubMenu = (subMenuItems, showSubMenu, parentId) => {
    if (!showSubMenu || !isSidebarOpen) return null;

    const iconSize = getIconSize();
    const fontSize = getFontSize();

    return (
      <div className="submenu-container ms-4 mt-1">
        {subMenuItems.map((subItem) => (
          <button
            key={subItem.id}
            className={`nav-link text-start d-flex align-items-center mb-2 w-100 submenu-item ${
              currentActiveTab === subItem.id ? 'active' : ''
            }`}
            onClick={() => handleSubMenuItemClick(subItem)}
            style={{ 
              borderRadius: '6px',
              border: `1px solid ${currentActiveTab === subItem.id ? primaryColor : 'transparent'}`,
              padding: deviceType === 'mobile' ? '0.6rem 0.8rem' : '0.5rem 0.8rem',
              transition: 'all 0.2s ease',
              fontSize: fontSize,
              fontWeight: '500',
              background: currentActiveTab === subItem.id ? 
                `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` : 'transparent',
              color: currentActiveTab === subItem.id ? 'white' : `${primaryColor}dd`,
            }}
          >
            <i 
              className={`${subItem.icon} me-2`}
              style={{ 
                fontSize: `calc(${iconSize} - 0.2rem)`,
                color: currentActiveTab === subItem.id ? 'white' : `${primaryColor}bb`,
                width: '16px',
                textAlign: 'center'
              }}
            ></i>
            {isSidebarOpen && <span>{subItem.label}</span>}
          </button>
        ))}
      </div>
    );
  };

  const renderNavItem = (item) => {
    const hasSubMenu = (item.id === 'modify-users' && userRole === 'admin') || 
                      (item.id === 'tasks' && (userRole === 'admin' || userRole === 'manager')) ||
                      (item.id === 'operator-tasks' && userRole === 'operator');
    
    const showSubMenu = (item.id === 'modify-users' && showModifySubMenu) || 
                       (item.id === 'tasks' && showTasksSubMenu) ||
                       (item.id === 'operator-tasks' && showTasksSubMenu);

    const subMenuItems = getSubMenuItems(item.id);
    const isParentActive = currentActiveTab.startsWith(item.id === 'modify-users' ? 'modify-' : 
                          item.id === 'tasks' ? 'tasks-' : 
                          item.id === 'operator-tasks' ? 'tasks-' : item.id);

    const isHovered = hoveredItem === item.id;
    const iconSize = getIconSize();
    const fontSize = getFontSize();

    if (hasSubMenu) {
      return (
        <div 
          className="nav-item-with-submenu"
          onMouseEnter={() => deviceType !== 'mobile' && setHoveredItem(item.id)}
          onMouseLeave={() => deviceType !== 'mobile' && setHoveredItem(null)}
        >
          <button
            className={`nav-link text-start d-flex align-items-center justify-content-between mb-2 w-100 menu-item ${
              isParentActive ? 'active' : ''
            }`}
            onClick={() => handleNavItemClick(item)}
            style={{ 
              borderRadius: '8px',
              border: `1px solid ${isParentActive ? primaryColor : 'transparent'}`,
              padding: isSidebarOpen ? 
                (deviceType === 'mobile' ? '0.8rem 1rem' : '0.7rem 1rem') : 
                (deviceType === 'mobile' ? '0.8rem' : '0.7rem'),
              transition: 'all 0.3s ease',
              fontSize: fontSize,
              fontWeight: '600',
              background: isParentActive ? 
                `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` : 
                (isHovered ? `linear-gradient(135deg, ${primaryColor}10, ${secondaryColor}10)` : 'transparent'),
              color: isParentActive ? 'white' : `${primaryColor}ee`,
              justifyContent: isSidebarOpen ? 'space-between' : 'center',
              minHeight: deviceType === 'mobile' ? '44px' : '40px'
            }}
            title={!isSidebarOpen ? item.label : ''}
          >
            <div className="d-flex align-items-center">
              <i 
                className={`${item.icon}`}
                style={{ 
                  color: isParentActive ? 'white' : primaryColor,
                  width: '20px',
                  textAlign: 'center',
                  fontSize: iconSize
                }}
              ></i>
              {isSidebarOpen && <span className="ms-3">{item.label}</span>}
            </div>
            {isSidebarOpen && (
              <i 
                className={`fas fa-chevron-${showSubMenu ? 'down' : 'right'} small`}
                style={{ 
                  color: isParentActive ? 'white' : `${primaryColor}99`,
                  fontSize: `calc(${iconSize} - 0.3rem)`,
                  transition: 'transform 0.3s ease'
                }}
              ></i>
            )}
          </button>

          {renderSubMenu(subMenuItems, showSubMenu, item.id)}
        </div>
      );
    } else {
      return (
        <div
          onMouseEnter={() => deviceType !== 'mobile' && setHoveredItem(item.id)}
          onMouseLeave={() => deviceType !== 'mobile' && setHoveredItem(null)}
        >
          <button
            className={`nav-link text-start d-flex align-items-center mb-2 w-100 menu-item ${
              currentActiveTab === item.id ? 'active' : ''
            }`}
            onClick={() => handleNavItemClick(item)}
            style={{ 
              borderRadius: '8px',
              border: `1px solid ${currentActiveTab === item.id ? primaryColor : 'transparent'}`,
              padding: isSidebarOpen ? 
                (deviceType === 'mobile' ? '0.8rem 1rem' : '0.7rem 1rem') : 
                (deviceType === 'mobile' ? '0.8rem' : '0.7rem'),
              transition: 'all 0.3s ease',
              fontSize: fontSize,
              fontWeight: '600',
              background: currentActiveTab === item.id ? 
                `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` : 
                (isHovered ? `linear-gradient(135deg, ${primaryColor}10, ${secondaryColor}10)` : 'transparent'),
              color: currentActiveTab === item.id ? 'white' : `${primaryColor}ee`,
              justifyContent: isSidebarOpen ? 'flex-start' : 'center',
              minHeight: deviceType === 'mobile' ? '44px' : '40px'
            }}
            title={!isSidebarOpen ? item.label : ''}
          >
            <i 
              className={`${item.icon}`} 
              style={{ 
                width: '20px',
                textAlign: 'center',
                color: currentActiveTab === item.id ? 'white' : primaryColor,
                fontSize: iconSize
              }}
            ></i>
            {isSidebarOpen && <span className="ms-3">{item.label}</span>}
          </button>
        </div>
      );
    }
  };

  return (
    <>
      {/* Mobile/Tablet Overlay */}
      {(deviceType === 'mobile' || deviceType === 'tablet') && isSidebarOpen && (
        <div 
          className="sidebar-overlay show"
          onClick={onToggleSidebar}
          style={{
            zIndex: 999
          }}
        />
      )}
      
      {/* Sidebar */}
      <div 
        className={`sidebar ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'} ${isSidebarOpen ? 'sidebar-expanded' : 'sidebar-collapsed'}`}
        style={{ 
          '--primary-color': primaryColor,
          '--secondary-color': secondaryColor,
          width: getSidebarWidth(),
          height: `calc(100vh - ${deviceType === 'mobile' ? '60px' : '56px'})`,
          top: deviceType === 'mobile' ? '60px' : '56px',
          zIndex: 1000
        }}
        onMouseEnter={() => deviceType === 'desktop' && !isSidebarOpen && onToggleSidebar()}
        onMouseLeave={() => deviceType === 'desktop' && isSidebarOpen && onToggleSidebar()}
      >
        <nav className="sidebar-nav">
          <div className="nav flex-column">
            {getMenuItems().map((item) => (
              <div key={item.id} className="nav-item-wrapper">
                {renderNavItem(item)}
              </div>
            ))}
          </div>
        </nav>
      </div>
    </>
  );
}

export default Sidebar;